// request_cycle (PA, nominated PH or RS; AA on sandboxes before 0016): ask for a new daily cycle. Not a signature and not a
// permission to work.
import { useState } from 'react'
import { Banner, KeyValue } from '@/components/ui'
import type { ActionPanelProps } from '@/features/contracts'
import { fromLocalInput, inputTimeArg, toLocalInput } from '@/lib/format'
import { usePolicy } from '@/lib/queries'
import { PanelFrame } from '../frame'
import { usePanelClock, usePanelCommand } from '../hooks'
import {
  currentAuthorisation,
  defaultCycleWindow,
  FALLBACK_NORMAL_VALIDATION_HOURS,
  findCycle,
  formatDuration,
  minutesBetween,
  msToIso,
  parseIsoMs,
  policyParameter,
  usableNumber,
} from '../logic'
import { CycleStateBadge, DateTimeField, PolicyNote, roleLabel, WindowText } from '../parts'

export function RequestCyclePanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const now = usePanelClock(bundle)
  const policy = usePolicy(bundle.policy.version_id)
  const run = usePanelCommand('request_cycle', bundle, onDone)
  const auth = currentAuthorisation(bundle)
  const current = findCycle(bundle, bundle.permit.current_cycle_id)
  const [initial] = useState(() => {
    const nowMs = now()
    // The default start is now itself (or the authorised start, if later), sent exactly
    // while the input still shows its minute. The next whole minute would start the cycle,
    // and every validation of it, up to a minute in the future: a quick acceptance was
    // then refused as outside the validated period.
    const start = Math.max(nowMs, parseIsoMs(auth?.authorisation_from) ?? nowMs)
    return { ...defaultCycleWindow(nowMs, auth), startExact: msToIso(start) }
  })
  const [from, setFrom] = useState(() => toLocalInput(initial.startExact))
  const [to, setTo] = useState(() => toLocalInput(msToIso(initial.to)))
  const [tried, setTried] = useState(false)

  const fromIso = inputTimeArg(from, initial.startExact)
  const toIso = fromLocalInput(to)
  const mins = minutesBetween(fromIso, toIso)
  const normalParam = policyParameter(policy.data, 'validation.normal_hours')
  const normalHours = usableNumber(normalParam) ?? FALLBACK_NORMAL_VALIDATION_HOURS
  const toMs = parseIsoMs(toIso)
  const fromMs = parseIsoMs(fromIso)
  const outsideAuthorisation =
    auth !== null &&
    ((fromMs !== null && fromMs < (parseIsoMs(auth.authorisation_from) ?? fromMs)) ||
      (toMs !== null && toMs > (parseIsoMs(auth.authorisation_to) ?? toMs)))

  const problems: string[] = []
  if (!fromIso) problems.push('Enter the cycle start.')
  if (!toIso) problems.push('Enter the cycle end.')
  if (mins !== null && mins <= 0) problems.push('The cycle end must be after its start.')

  function submit() {
    setTried(true)
    if (problems.length > 0 || !fromIso || !toIso) return
    void run.submit({ permit_id: bundle.permit.id, requested_from: fromIso, requested_to: toIso })
  }

  return (
    <PanelFrame action={action} run={run} problems={problems} tried={tried} onCancel={onCancel} onSubmit={submit} submitLabel="Request cycle">
      <p className="mb-4 text-sm text-ink">
        Ask for a new daily cycle on permit <span className="code">{bundle.permit.permit_number}</span>. Asking does not allow
        work: the RS pre-issues the cycle, an RS validates it and the PH accepts it first.
      </p>
      <div className="mb-4 rounded-lg border border-line bg-white p-3">
        <KeyValue
          items={[
            ['Authorised period', auth ? <WindowText from={auth.authorisation_from} to={auth.authorisation_to} /> : 'No current authorisation'],
            [
              'Current cycle',
              current ? (
                <span className="flex flex-wrap items-center gap-2">
                  Cycle {current.cycle_number} <CycleStateBadge state={current.cycle_state} />
                </span>
              ) : (
                'None yet'
              ),
            ],
            ['You ask as', roleLabel(action.role)],
          ]}
        />
        {current ? (
          <p className="mt-2 text-xs text-ink-soft">
            A new cycle replaces cycle {current.cycle_number}. Its signatures stay on the record and do not carry over.
          </p>
        ) : null}
      </div>

      <div className="grid gap-x-4 sm:grid-cols-2">
        <DateTimeField label="Cycle start" required value={from} onChange={setFrom} />
        <DateTimeField label="Cycle end" required value={to} onChange={setTo} hint="Default: 10 hours, inside the authorised period." />
      </div>
      <p className="mb-4 text-sm text-ink">
        Length: <strong>{mins !== null ? formatDuration(mins) : '-'}</strong>
      </p>

      {mins !== null && mins > normalHours * 60 ? (
        <Banner tone="warn" title={`Longer than the normal ${normalHours}-hour validation period`} className="mb-4">
          A longer period has no approved route yet, so expect the server to refuse it. <PolicyNote param={normalParam} code="validation.normal_hours" />
        </Banner>
      ) : null}
      {outsideAuthorisation ? (
        <Banner tone="warn" title="Outside the authorised period" className="mb-4">
          The cycle must fit inside the period the RS authorised.
        </Banner>
      ) : null}
      {toMs !== null && toMs <= now() ? (
        <Banner tone="warn" title="The end time has already passed" className="mb-4">
          Choose an end time in the future.
        </Banner>
      ) : null}
    </PanelFrame>
  )
}
